/** MSME-simplified UI copy and visibility helpers. */

/** Linear MSME journey: Bills → Footprint → One report goal. Ops/connectors stay in sidebar. */
export const MSME_LINEAR_JOURNEY = {
  title: 'Your path',
  subtitle: 'Profile and operational boundary, then bills → footprint → one report goal.',
  steps: [
    { id: 'documents', label: 'Upload bills', path: '/document-upload' },
    { id: 'assessment', label: 'See footprint', path: '/carbon-footprint?section=start-assessment' },
    { id: 'report', label: 'Pick report goal', path: '/reporting' },
  ],
} as const;

export const MSME_ADVANCED_SECTION_TITLE = 'More tools';
export const MSME_ADVANCED_SECTION_SUBTITLE =
  'Operations, data connectors, and full compliance frameworks — open anytime from here or the sidebar.';

export const MSME_OPS_QUEST_TITLE = 'Operations quests';
export const MSME_OPS_QUEST_SUBTITLE =
  'Fill a few monthly numbers — unlock levels as your energy and meters get clearer.';
export const MSME_OPS_FRIENDLY_TITLE = 'Energy and meters';
export const MSME_OPS_FRIENDLY_SUBTITLE =
  'Add electricity and fuel, name the site, then complete the Scope 1–3 operational boundary below. Both are required.';
export const MSME_OPS_ADVANCED_DETAIL_TITLE = 'Operational boundary — ISO 14064-1 and GHG Protocol Scopes 1–3';
export const MSME_OPS_ADVANCED_DETAIL_SUBTITLE =
  'Required. Set direct emissions, imported energy, and each Scope 3 category before the inventory is calculated.';
export const MSME_REGISTRATION_OPS_TITLE = 'Energy and meters (with your profile)';
export const MSME_REGISTRATION_OPS_HINT =
  'Optional ~5 min with company profile: name your site and add electricity or fuel. Bills still come next.';

export const MSME_DATA_QUEST_TITLE = 'Data quests';
export const MSME_DATA_QUEST_SUBTITLE = 'Upload bills and unlock levels as evidence lands.';
export const MSME_EMISSIONS_QUEST_TITLE = 'Footprint quests';
export const MSME_EMISSIONS_QUEST_SUBTITLE = 'Measure, explore hotspots, then pick a reduction idea.';
export const MSME_COMPLIANCE_QUEST_TITLE = 'Compliance quests';
export const MSME_COMPLIANCE_QUEST_SUBTITLE = 'Close readiness gaps, then pick one report goal.';
export const MSME_FINANCE_QUEST_TITLE = 'Government schemes';
export const MSME_FINANCE_QUEST_SUBTITLE = 'Apply on the official portals. This workspace does not file applications.';

export const MSME_REPORTING_INTENT_STORAGE_KEY = 'msme_reporting_intent_v1';

export function getStoredMsmeReportGoal(serverIntent?: string | null): string | null {
  const fromServer = typeof serverIntent === 'string' && serverIntent.trim() ? serverIntent.trim() : null;
  if (fromServer) {
    return fromServer;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const value = window.localStorage.getItem(MSME_REPORTING_INTENT_STORAGE_KEY);
    return value && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
}

export function setStoredMsmeReportGoal(intentId: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(MSME_REPORTING_INTENT_STORAGE_KEY, intentId);
  } catch {
    // ignore quota / private mode
  }
}

/** Persist report goal locally and on MSME profile (agentic journey continuity). */
export async function persistMsmeReportGoal(
  intentId: string,
  updateProfile?: (payload: { uxPreferences: { reportingIntent: string } }) => Promise<unknown>
): Promise<void> {
  setStoredMsmeReportGoal(intentId);
  if (!updateProfile) {
    return;
  }
  try {
    await updateProfile({ uxPreferences: { reportingIntent: intentId } });
  } catch {
    // Local storage remains the offline fallback
  }
}

export const MSME_SIMPLIFIED_SECTION_TITLE = 'What to do next';
export const MSME_SIMPLIFIED_GAP_TITLE = 'Items that can raise your scores';
export const MSME_SIMPLIFIED_GAP_SUBTITLE =
  'We only show gaps that still need your attention. If you already uploaded bills, we link you to review them instead of asking again.';

export const MSME_READINESS_CHECKLIST_TITLE = 'Your 3 steps';
export const MSME_READINESS_CHECKLIST_SUBTITLE =
  'Do these in order. Tap the green button on each step.';
export const MSME_READINESS_CHECKLIST_ALL_GAPS = 'See all gaps and actions';
export const MSME_READINESS_CHECKLIST_COMPLETE =
  'Looks good — check again after new bills or a buyer request.';

export const MSME_REPORTING_OVERVIEW_TITLE = 'Your reporting status';
export const MSME_REPORTING_OVERVIEW_SUBTITLE =
  'A quick view of India and export readiness. Follow the steps below — usually about 1 minute per check.';

/** Score at which "Prepare all reports" is offered in simplified mode. */
export const MSME_PREPARE_REPORTS_THRESHOLD = 65;

export const MSME_SIMPLIFIED_ACTION_LABELS = {
  checkReadiness: 'Check my readiness',
  matchBills: 'Match my bills automatically',
  prepareAllReports: 'Prepare all my reports',
  checkReadinessFirst: 'Check readiness first',
  uploadBills: 'Upload bills',
  helpReportReady: 'Help me get report-ready',
  helpReportReadyHint: 'We scan your bills and forms — usually about 1 minute.',
  methodologyExportNote: 'Full methodology is included when you download your ISO 14064 PDF.',
  expertModeHint: 'For auditors, consultants, or export compliance teams.',
  ownershipNeedsAnswer: 'Needs answer',
  maintenanceSettingsTitle: 'How we estimate maintenance costs',
} as const;

/** True when blocked reason text suggests missing uploads rather than readiness scoring. */
export const isMsmeDataGapReason = (text = ''): boolean => {
  const normalized = String(text || '').toLowerCase();
  return /upload|bill|document|ocr|utility|electricity|fuel|meter|kwh|litre|liter|invoice|receipt/.test(normalized);
};

export const msmeExportPrimaryAction = (
  primaryGap: { label: string; link: string } | null
): { label: string; type: 'link' | 'orchestrate'; link?: string } => {
  if (!primaryGap) {
    return { label: MSME_SIMPLIFIED_ACTION_LABELS.checkReadinessFirst, type: 'orchestrate' };
  }
  if (isMsmeDataGapReason(primaryGap.label)) {
    const link = /document|upload|bill/.test(primaryGap.link) ? primaryGap.link : '/document-upload';
    return { label: MSME_SIMPLIFIED_ACTION_LABELS.uploadBills, type: 'link', link };
  }
  return { label: MSME_SIMPLIFIED_ACTION_LABELS.checkReadinessFirst, type: 'orchestrate' };
};

/** Hide technical panels on MSME reporting overview. */
export const MSME_HIDE_REPORTING_PANELS = {
  remediationTrackDetails: true,
  agentAdvisoryDetails: true,
  blendedScoreBreakdown: true,
  sectorLensAlert: true,
} as const;

/**
 * Hide technical panels on MSME reporting overview in simple mode.
 * Expert mode surfaces the same technical panels as the non-MSME reporting view.
 */
export const shouldHideReportingPanel = (
  panel: keyof typeof MSME_HIDE_REPORTING_PANELS,
  isMsmeSimplified: boolean,
  expertMode: boolean
): boolean => {
  if (!isMsmeSimplified) {
    return false;
  }
  return MSME_HIDE_REPORTING_PANELS[panel] && !expertMode;
};

export const msmeSimplifiedActionLabel = (action: string, dataFeedSatisfied?: boolean): string => {
  if (!dataFeedSatisfied) {
    return action;
  }
  if (/upload/i.test(action)) {
    return action.replace(/upload/gi, 'Review');
  }
  return action;
};
